import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AutoResume, DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { BehaviorSubject, finalize } from 'rxjs';
import { environment } from '@uw-env/environment';
import { AuthService } from './auth.service';
import {
  IdleWarningDialogComponent,
  IdleWarningDialogData,
  IdleWarningResult
} from '../../shared/components/idle-warning-dialog/idle-warning-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class IdleLogoutService {

  private _initialized = false;

  private _warningSeconds = 0;

  private _dialogRef: MatDialogRef<IdleWarningDialogComponent, IdleWarningResult> | null = null;

  private readonly _secondsRemaining$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly _idle: Idle,
    private readonly _dialog: MatDialog,
    private readonly _authService: AuthService,
    private readonly _ngZone: NgZone
  ) { }

  init(): void {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    const { inactivityTimeoutMinutes, warningSeconds } = environment.ngIdleConfig;
    const total = Math.round(inactivityTimeoutMinutes * 60);
    this._warningSeconds = Math.min(warningSeconds, total - 1);

    this._idle.setIdle(total - this._warningSeconds);
    this._idle.setTimeout(this._warningSeconds);
    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // Once the warning is shown, activity must not dismiss it; only the dialog buttons can.
    this._idle.setAutoResume(AutoResume.notIdle);

    this._idle.onIdleStart.subscribe(() => this._ngZone.run(() => this.onIdleStart()));
    this._idle.onTimeoutWarning.subscribe((seconds: number) =>
      this._ngZone.run(() => this._secondsRemaining$.next(seconds))
    );
    this._idle.onIdleEnd.subscribe(() => this._ngZone.run(() => this.closeDialog()));
    this._idle.onTimeout.subscribe(() => this._ngZone.run(() => this.onTimeout()));

    this._idle.watch();
  }

  private isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  private onIdleStart(): void {
    if (!this.isLoggedIn()) {
      this._idle.watch();
      return;
    }
    if (this._dialogRef) {
      return;
    }

    this._secondsRemaining$.next(this._warningSeconds);

    const dialogRef = this._dialog.open<IdleWarningDialogComponent, IdleWarningDialogData, IdleWarningResult>(
      IdleWarningDialogComponent,
      {
        panelClass: 'uw-dialog-panel',
        disableClose: true,
        autoFocus: 'first-tabbable',
        data: { secondsRemaining$: this._secondsRemaining$.asObservable() }
      }
    );
    this._dialogRef = dialogRef;

    dialogRef.afterClosed().subscribe(result => {
      if (this._dialogRef === dialogRef) {
        this._dialogRef = null;
      }
      if (result === 'stay') {
        this._idle.watch();
      } else if (result === 'logout') {
        this._idle.stop();
        this.forceLogout();
      }
    });
  }

  private onTimeout(): void {
    this.closeDialog();
    if (this.isLoggedIn()) {
      this.forceLogout();
    }
    this._idle.watch();
  }

  private closeDialog(): void {
    this._dialogRef?.close();
  }

  private forceLogout(): void {
    const user = this._authService.getUser();
    const isSSOUser = user?.isSSOUser ?? localStorage.getItem('isSSOUser') === 'true';
    const lang = localStorage.getItem('lang');

    if (!user) {
      localStorage.clear();
      this.redirectToLogin(isSSOUser, lang);
      return;
    }

    this._authService.logOut().pipe(
      finalize(() => {
        localStorage.clear();
        this.redirectToLogin(isSSOUser, lang);
      })
    ).subscribe({ error: () => { } });
  }

  private redirectToLogin(isSSOUser: boolean, lang: string | null): void {
    if (isSSOUser) {
      window.location.href = `${environment.donorSiteUrl}/main/sso-login?loggedout=true&isSSOUser=${isSSOUser}&lang=${lang}`;
    } else {
      window.location.href = `${environment.donorSiteUrl}?loggedout=true&lang=${lang}`;
    }
  }
}
